import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.function.name.js";
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import config from 'utils/config';
import getLikeLabel from 'utils/i18n/getLikeLabel';
import getContentItemType, { getContentItemTypeTranslationKey } from 'utils/asset/getContentItemType';
import getContentProgress from 'utils/asset/getContentProgress';
import { FILE_STATUS_VALUES } from 'actions/file';
/* Components */

import Modal from 'components/Modal';
import Box from 'components/Box';
import Button from 'components/Button';
import Flex from 'components/Flex';
import Stack from 'components/Stack';
import Heading from 'components/Heading';
import useBreakpoints from 'hooks/useBreakpoints';
import ToolbarButton from 'components/ToolbarButton';
import TooltipButton from 'components/TooltipButton';
import ThemeSection from 'components/ThemeSection';
import InlineList from 'components/InlineList';
import { TooltipPillWithCount, GoalPill, ContentProgressPill } from 'components/Pill';
import * as Layout from './Layout';
import useFullscreen from './hooks/useFullscreen';
var ActionGrid = Layout.ActionGrid,
  ActionGridButton = Layout.ActionGridButton,
  SidebarButton = Layout.SidebarButton;
export { ActionGrid, ActionGridButton, SidebarButton };
function AssetViewerUI(props) {
  var asset = props.asset,
    autoCompleteEnabled = props.autoCompleteEnabled,
    _props$renderContent = props.renderContent,
    renderContent = _props$renderContent === void 0 ? function () {
      return null;
    } : _props$renderContent,
    _props$renderComments = props.renderComments,
    renderComments = _props$renderComments === void 0 ? function () {
      return null;
    } : _props$renderComments,
    _props$renderActions = props.renderActions,
    renderActions = _props$renderActions === void 0 ? function () {
      return null;
    } : _props$renderActions,
    _props$renderFullscre = props.renderFullscreenActions,
    renderFullscreenActions = _props$renderFullscre === void 0 ? function () {
      return null;
    } : _props$renderFullscre,
    _props$renderInfoSect = props.renderInfoSection,
    renderInfoSection = _props$renderInfoSect === void 0 ? function () {
      return null;
    } : _props$renderInfoSect,
    _props$renderPlaylist = props.renderPlaylistInfo,
    renderPlaylistInfo = _props$renderPlaylist === void 0 ? function () {
      return null;
    } : _props$renderPlaylist,
    _props$renderNavigato = props.renderNavigator,
    renderNavigator = _props$renderNavigato === void 0 ? function () {
      return null;
    } : _props$renderNavigato,
    onClose = props.onClose;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var assetType = getContentItemType(asset);
  var basicAssetType = getContentItemType(asset, false);
  var assetMustBeDownloaded = asset.download === 'required';
  var isAssetReady = asset.published && (asset.type === 'web' || asset.fileStatus === FILE_STATUS_VALUES.READY || assetMustBeDownloaded);
  var stageRef = useRef(null);
  var _useFullscreen = useFullscreen(stageRef),
    toggleNativeFullscreen = _useFullscreen.toggleFullscreen;
  var _useBreakpoints = useBreakpoints(),
    isDesktop = _useBreakpoints[Layout.assetViewerBreakpoint];
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isSidebarCollapsed = _useState2[0],
    setSidebarCollapsed = _useState2[1];
  var onToggleSidebar = function onToggleSidebar() {
    return setSidebarCollapsed(function (cur) {
      return !cur;
    });
  };
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isFakeMobileFullscreen = _useState4[0],
    setFakeMobileFullscreen = _useState4[1];
  var onToggleFakeMobileFullscreen = function onToggleFakeMobileFullscreen() {
    setSidebarCollapsed(false);
    setFakeMobileFullscreen(function (cur) {
      return !cur;
    });
  };
  var isFullscreen = !isDesktop || isSidebarCollapsed || isFakeMobileFullscreen;
  var canCollapseSidebar = isSidebarCollapsed && isDesktop && isFullscreen;

  // Older iOS browsers don't support the fullscreen web API, so
  // for those we toggle a "fake" fullscreen view with a reduced UI
  function toggleFullscreen() {
    if (toggleNativeFullscreen) {
      toggleNativeFullscreen();
    } else {
      onToggleFakeMobileFullscreen();
    }
  }
  var isNonEmbeddedWebLink = basicAssetType === 'web' && !asset.openInIframe;
  var titleId = "asset_viewer_title_".concat(asset.id);

  // On mobile, we don't want to show the fullscreen button
  // for asset types audio, video, web links and downloads
  var hasMobileFullscreenButton = !isDesktop && !['audio', 'video'].includes(basicAssetType) && !assetMustBeDownloaded && !isNonEmbeddedWebLink && assetType !== 'video';
  var allowFlexibleStageHeight = (['audio', 'web'].includes(basicAssetType) || asset.download === 'required') && !asset.openInIframe;
  var progress = getContentProgress({
    userContentStatus: asset.userContentStatus,
    userScormAssetStatus: asset.userScormAssetStatus
  });
  return /*#__PURE__*/React.createElement(Modal, {
    id: "asset-viewer",
    "aria-labelledby": titleId,
    layout: "fullscreen",
    spacing: isFullscreen ? null : 'm',
    onRequestClose: onClose
  }, /*#__PURE__*/React.createElement(ThemeSection, {
    name: "card"
  }, /*#__PURE__*/React.createElement(Layout.Wrapper, {
    maxWidth: isFullscreen ? null : 1580,
    mx: "auto",
    isFullscreen: isFullscreen,
    isFakeMobileFullscreen: isFakeMobileFullscreen
  }, !isDesktop && !isFakeMobileFullscreen && /*#__PURE__*/React.createElement(Layout.MobileTitleBar, null, /*#__PURE__*/React.createElement(Flex, {
    borderBottom: "thin"
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(ToolbarButton, {
    icon: "x",
    title: t('asset_viewer_close'),
    onClick: onClose
  })), /*#__PURE__*/React.createElement(Box, {
    grow: true,
    shrink: true,
    bold: true,
    as: "p",
    overflow: "ellipsis"
  }, t(getContentItemTypeTranslationKey(assetType))))), isFakeMobileFullscreen && /*#__PURE__*/React.createElement(Layout.FakeFullscreenButtonWrapper, null, /*#__PURE__*/React.createElement(Button, {
    square: true,
    variant: "transparent",
    icon: "fullscreen-exit",
    title: t('actions_exit_fullscreen'),
    onClick: toggleFullscreen
  })), /*#__PURE__*/React.createElement(Layout.StageWrapper, {
    isFakeMobileFullscreen: isFakeMobileFullscreen,
    sticky: !allowFlexibleStageHeight
  }, /*#__PURE__*/React.createElement(Layout.Stage, {
    ref: stageRef,
    sticky: !allowFlexibleStageHeight,
    tabIndex: 0,
    "data-autofocus": true
  }, renderContent()), hasMobileFullscreenButton && !isFakeMobileFullscreen && /*#__PURE__*/React.createElement(Layout.MobileFullscreenButtonWrapper, null, /*#__PURE__*/React.createElement(Button, {
    icon: "fullscreen",
    size: "medium",
    onClick: toggleFullscreen,
    mb: "-m"
  }, t('actions_fullscreen')))), !isFakeMobileFullscreen && (!canCollapseSidebar ? /*#__PURE__*/React.createElement(Layout.Sidebar, null, /*#__PURE__*/React.createElement(Stack, {
    withDividers: true,
    spacing: 0
  }, /*#__PURE__*/React.createElement(Box, {
    p: "m"
  }, /*#__PURE__*/React.createElement(Heading, {
    as: "h1",
    size: "small",
    mb: "xxs",
    mr: isDesktop ? 80 : null,
    id: titleId
  }, asset.name), /*#__PURE__*/React.createElement(InlineList.Wrapper, {
    spacing: "xs"
  }, asset.goal && asset.goal.type === 'complete' && /*#__PURE__*/React.createElement(InlineList.Item, null, /*#__PURE__*/React.createElement(GoalPill, {
    goal: asset.goal
  })), /*#__PURE__*/React.createElement(InlineList.Item, null, /*#__PURE__*/React.createElement(TooltipPillWithCount, {
    icon: "like",
    count: asset.rating_count,
    highlightIcon: asset.user_rated_like,
    title: getLikeLabel(asset.rating_count, asset.user_rated_like)
  })), config.enableComments && /*#__PURE__*/React.createElement(InlineList.Item, null, /*#__PURE__*/React.createElement(TooltipPillWithCount, {
    "data-qa": "comment-count-pill",
    icon: "chat",
    count: asset.commentCount,
    highlightIcon: asset.commentCountUser > 0,
    title: t('comment_count', {
      count: asset.commentCount
    })
  })), /*#__PURE__*/React.createElement(InlineList.Item, null, autoCompleteEnabled && /*#__PURE__*/React.createElement(ContentProgressPill, {
    contentName: asset.name,
    progress: progress
  }))), isDesktop && /*#__PURE__*/React.createElement(Box, {
    position: "absolute",
    right: 12,
    top: 12
  }, /*#__PURE__*/React.createElement(TooltipButton, {
    square: true,
    variant: "transparent",
    icon: "fullscreen",
    title: t('actions_fullscreen'),
    onClick: onToggleSidebar
  }), /*#__PURE__*/React.createElement(TooltipButton, {
    square: true,
    variant: "transparent",
    icon: "x",
    title: t('asset_viewer_close'),
    onClick: onClose
  })), renderInfoSection()), renderPlaylistInfo(), isAssetReady && renderActions(), renderComments()), renderNavigator()) : /*#__PURE__*/React.createElement(Layout.Sidebar, {
    isCollapsed: true
  }, /*#__PURE__*/React.createElement(SidebarButton, {
    icon: "x",
    title: t('asset_viewer_close'),
    tooltipPlacement: "left",
    onClick: onClose
  }), isAssetReady && renderFullscreenActions(), /*#__PURE__*/React.createElement(SidebarButton, {
    icon: "fullscreen-exit",
    title: t('actions_exit_fullscreen'),
    tooltipPlacement: "left",
    onClick: onToggleSidebar
  }))))));
}
export default AssetViewerUI;